import React from 'react';
import { connect } from 'react-redux';

import { getConfig } from '@edx/frontend-platform';
import { sendPageEvent, sendTrackEvent } from '@edx/frontend-platform/analytics';
import { injectIntl } from '@edx/frontend-platform/i18n';
import {
  Form, Hyperlink, Icon, StatefulButton,
} from '@edx/paragon';
import { Institution } from '@edx/paragon/icons';
import { EnvelopeIcon } from "@phosphor-icons/react";
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import Skeleton from 'react-loading-skeleton';
import { Link } from 'react-router-dom';

import HintedSSOOnboarding from './components/HintedSSOOnboarding';
import {
  loginRemovePasswordResetBanner, loginRequest, loginRequestFailure, loginRequestReset, setLoginFormData,
} from './data/actions';
import { INACTIVE_USER, INVALID_FORM, TPA_AUTHENTICATION_FAILURE } from './data/constants';
import EmailVerification from '../register/steps/EmailVerification';
import RegistrationConfirmation from '../register/steps/RegistrationConfirmation';
import AdditionalUserInformation from '../register/steps/AdditionalUserInformation';
import RegistrationProcessCompletion from '../register/steps/RegistrationProcessCompletion';
import { VALID_EMAIL_REGEX } from '../data/constants';
import { loginErrorSelector, loginFormDataSelector, loginRequestSelector } from './data/selectors';
import LoginFailureMessage from './LoginFailure';
import messages from './messages';
import {
  FormGroup, InstitutionLogistration, PasswordField, RedirectLogistration,
  RenderInstitutionButton, SocialAuthProviders,
  ThirdPartyAuthAlert,
} from '../common-components';
import { getThirdPartyAuthContext, clearRedirectTarget, setAuthenticatedUser } from '../common-components/data/actions';
import { thirdPartyAuthContextSelector } from '../common-components/data/selectors';
import EnterpriseSSO from '../common-components/EnterpriseSSO';
import Separator from '../common-components/Separator';
import {
  DEFAULT_STATE, ENTERPRISE_LOGIN_URL, PENDING_STATE,
  RESET_PAGE, LOGIN_PAGE,
} from '../data/constants';
import {
  getAllPossibleQueryParams,
  getTpaHint,
  getTpaProvider,
  updatePathWithQueryParams,
} from '../data/utils';
import { initGtmOnce, sendToGtmDataLayer } from './data/gtm';


class LoginPage extends React.Component {
  constructor(props, context) {
    super(props, context);
    this.state = {
      isFirstStep: true,
      isSecondStep: false,
      password: this.props.loginFormData.password,
      emailOrUsername: this.props.loginFormData.emailOrUsername,
      errors: {
        emailOrUsername: this.props.loginFormData.errors.emailOrUsername,
        password: this.props.loginFormData.errors.password,
      },
      inactiveUserStep: 'otp_verification',
      showInactiveFlow: true,
    };
    this.queryParams = getAllPossibleQueryParams();
    this.tpaHint = getTpaHint();
  }

  // helper to sync redux state to local state
  syncFromProps = (props) => {
    const loginFormData = props.loginFormData || {};
    const updatedState = {
      ...loginFormData,
      emailOrUsername: loginFormData.emailOrUsername ?? this.state.emailOrUsername,
      password: loginFormData.password ?? this.state.password,
    };

    const hasStateChanged =
      updatedState.emailOrUsername !== this.state.emailOrUsername ||
      updatedState.password !== this.state.password;

    if (hasStateChanged) this.setState(updatedState);
  };

  componentDidMount() {
    initGtmOnce(getConfig());
    sendPageEvent('login_and_registration', 'login');
    const payload = { ...this.queryParams };
    const { redirectTarget } = this.props;

    this.syncFromProps(this.props);

     // If redirected here, handle it after we've synced state
    if (redirectTarget === LOGIN_PAGE) {
      this.handleRedirect();
    }

    if (this.tpaHint) {
      payload.tpa_hint = this.tpaHint;
    }

    this.props.getThirdPartyAuthContext(payload);
    this.props.loginRequestReset();
  }

  componentDidUpdate(prevProps) {
    const { loginFormData, loginError, redirectTarget, thirdPartyAuthContext } = this.props;

    if (loginFormData !== prevProps.loginFormData) {
      this.syncFromProps(this.props);
    }

    if (loginError !== prevProps.loginError && loginError?.errorCode === INACTIVE_USER) {
      this.setState({ showInactiveFlow: true });
    }

    if (redirectTarget !== prevProps.redirectTarget && redirectTarget === LOGIN_PAGE) {
      this.handleRedirect();
    }

    // for tpa-saml provider at autentication pipline, when user comes back from IdP,
    // we need to move to second step to let him complete the process
    const hadNoProvider = !prevProps.thirdPartyAuthContext?.currentProvider;
    const hasProviderNow = thirdPartyAuthContext?.currentProvider;
    if (hadNoProvider && hasProviderNow && this.state.isFirstStep) {
      this.setState({ isFirstStep: false, isSecondStep: true });
    }

    // Reactivating an inactive account while a third-party-auth pipeline (e.g.
    // "bind my Google account to this existing, inactive user") is in progress:
    // see the 'otp_verification' step's handleGoToNextStep below, which sets
    // inactiveUserStep to 'completing_pipeline' and re-fetches the third-party
    // auth context now that the account is active and the user is logged in.
    // Once that (re-fetched) context has a finishAuthUrl, redirect there to
    // actually complete the pending SSO link - previously this case fell
    // through to the plain registration-completion wizard instead, which has
    // no way to finish an SSO pipeline, leaving the provider never linked and
    // the user unauthenticated at the end.
    //
    // Deliberately not gated on finishAuthUrl having *changed* from its
    // pre-login value - the backend may legitimately return the identical URL
    // before and after authentication, with only its usability (not its
    // string value) depending on the now-logged-in session. This effect can
    // therefore fire more than once while inactiveUserStep stays
    // 'completing_pipeline', but window.location.href assignment is
    // idempotent and the page navigates away on the first call regardless.
    if (this.state.inactiveUserStep === 'completing_pipeline' && thirdPartyAuthContext?.finishAuthUrl) {
      window.location.href = getConfig().LMS_BASE_URL + thirdPartyAuthContext.finishAuthUrl;
    }
  }

  handleRedirect = () => {
    this.setState({ isFirstStep: false, isSecondStep: true });
    if (typeof this.props.clearRedirectTarget === 'function') {
      this.props.clearRedirectTarget();
    }
  };

  componentWillUnmount() {
    if (this.props.resetPassword) {
      this.props.loginRemovePasswordResetBanner();
    }
  }

  getEnterPriseLoginURL() {
    return getConfig().LMS_BASE_URL + ENTERPRISE_LOGIN_URL;
  }

  handleSubmit = (e) => {
    e.preventDefault();
    if (this.props.resetPassword) {
      this.props.loginRemovePasswordResetBanner();
    }

    const { emailOrUsername, password } = this.state;
    const emailValidationError = this.validateEmail(emailOrUsername);
    const passwordValidationError = this.validatePassword(password);

    if (emailValidationError !== '' || passwordValidationError !== '') {
      this.props.setLoginFormData({
        errors: {
          emailOrUsername: emailValidationError,
          password: passwordValidationError,
        },
      });
      this.props.loginRequestFailure({
        errorCode: INVALID_FORM,
      });
      return;
    }

    this.props.loginRequestFailure(null);

    const payload = {
      email_or_username: emailOrUsername, password, ...this.queryParams,
    };
    this.props.loginRequest(payload);
  };

  handleStepChange = (e) => {
    e.preventDefault();
    sendToGtmDataLayer('click_on_mail_login')
        
    this.setState({ isFirstStep: false, isSecondStep: true });
  };

  handleOnBlur = (e) => {
    const payload = {
      [e.target.name]: e.target.value,
    };
    this.props.setLoginFormData(payload);
  };

  handleForgotPasswordLinkClickEvent = () => {
    sendTrackEvent('edx.bi.password-reset_form.toggled', { category: 'user-engagement' });
  };

  validateEmail(email) {
    const { errors } = this.state;
    const emailRegex = new RegExp(VALID_EMAIL_REGEX, 'i');   
    if (email === '') {
      errors.emailOrUsername = this.props.intl.formatMessage(messages['email.validation.message']);
    } else if (!emailRegex.test(email)) {
      errors.emailOrUsername = this.props.intl.formatMessage(messages['email.invalid.format.error']);
    } else {
      errors.emailOrUsername = '';
    }
    return errors.emailOrUsername;
  }

  validatePassword(password) {
    const { errors } = this.state;
    errors.password = password.length > 0 ? '' : this.props.intl.formatMessage(messages['password.validation.message']);

    return errors.password;
  }

  renderThirdPartyAuth(providers, secondaryProviders, currentProvider, thirdPartyAuthApiStatus, intl) {
    const isInstitutionAuthActive = !!secondaryProviders.length && !currentProvider;
    const isSocialAuthActive = !!providers.length && !currentProvider;
    const isEnterpriseLoginDisabled = getConfig().DISABLE_ENTERPRISE_LOGIN;
    const isThirdPartyAuthActive = isSocialAuthActive || (isEnterpriseLoginDisabled && isInstitutionAuthActive);

    return (
      <>
        {(!isEnterpriseLoginDisabled && isSocialAuthActive) && (
          <Hyperlink className="btn btn-link btn-sm text-body p-0 mb-4" destination={this.getEnterPriseLoginURL()}>
            <Icon src={Institution} className="institute-icon" />
            {intl.formatMessage(messages['enterprise.login.btn.text'])}
          </Hyperlink>
        )}

        {thirdPartyAuthApiStatus === PENDING_STATE && isThirdPartyAuthActive ? (
          <Skeleton className="tpa-skeleton mb-3" height={30} count={2} />
        ) : (
          <>
            {(isEnterpriseLoginDisabled && isInstitutionAuthActive) && (
              <RenderInstitutionButton
                onSubmitHandler={this.props.handleInstitutionLogin}
                buttonTitle={intl.formatMessage(messages['institution.login.button'])}
              />
            )}
            {isSocialAuthActive && (
              <div className="social-auth-providers form-w-third-party-auth flex-center-column w-100 the-new-login-button-campus">
                <SocialAuthProviders socialAuthProviders={providers} style={{ color: 'white' }} />
              </div>
            )}
          </>
        )}
      </>
    );
  }

  renderForm(
    currentProvider,
    providers,
    secondaryProviders,
    thirdPartyAuthContext,
    thirdPartyAuthApiStatus,
    submitState,
    intl,
  ) {
    if (this.props.institutionLogin) {
      return (
        <InstitutionLogistration
          secondaryProviders={thirdPartyAuthContext.secondaryProviders}
          headingTitle={intl.formatMessage(messages['institution.login.page.title'])}
        />
      );
    }
    const tpaAuthenticationError = {};
    if (thirdPartyAuthContext.errorMessage) {
      tpaAuthenticationError.context = {
        errorMessage: thirdPartyAuthContext.errorMessage,
      };
      tpaAuthenticationError.errorCode = TPA_AUTHENTICATION_FAILURE;
    }

    return (
      <>
        <Helmet>
          <title>{intl.formatMessage(messages['login.page.tab.title'],
            { siteName: getConfig().SITE_NAME })}
          </title>
        </Helmet>
        <RedirectLogistration
          success={this.props.loginResult.success}
          redirectUrl={this.props.loginResult.redirectUrl}
          finishAuthUrl={thirdPartyAuthContext.finishAuthUrl}
        />
        <div className="form-w">
          <div className='d-flex flex-column w-100 h-100 form-login-register-wrapper'> 
            <div className='d-flex flex-column justify-content-center align-items-center form-login-register-background'> 
              <ThirdPartyAuthAlert
                currentProvider={thirdPartyAuthContext.currentProvider}
                platformName={thirdPartyAuthContext.platformName}
              />
              {thirdPartyAuthContext.errorMessage ? <LoginFailureMessage loginError={tpaAuthenticationError} /> : null}
              <Form className="sign-in-form the-new-login-style-campus" id="sign-in-form">
                <div className={`the-new-login-style-campus-all-box flex-center-column`}>
                  <h1 className="page-title mb-0 the-new-login-style-campus-h1">
                    {Boolean(this.state.isFirstStep) && (
                      intl.formatMessage(messages['login.page.first.step.title'], { enter: <br /> })
                    )}
                    {Boolean(this.state.isSecondStep) && (
                      intl.formatMessage(messages['login.page.title'], { enter: <br /> })
                    )}                
                  </h1>
                  <h2 className="the-new-login-style-campus-h2">
                    {Boolean(this.state.isFirstStep) && (
                      intl.formatMessage(messages['login.page.first.step.subtitle'], { enter: <br /> })
                    )}
                    {Boolean(this.state.isSecondStep) && (
                      intl.formatMessage(messages['login.page.subtitle'], { siteName: getConfig().SITE_NAME })
                    )}
                  </h2>
                  <div className="form-w-third-party-auth w-100">
                    {Boolean(this.state.isFirstStep) && (
                      this.renderThirdPartyAuth(
                        providers,
                        secondaryProviders,
                        currentProvider,
                        thirdPartyAuthApiStatus,
                        intl,
                      )
                    )}
                    {Boolean(this.state.isFirstStep) && (
                      <>
                      <Separator style={{ color: 'black', margin: '25px 0 25px' }} />
                      <div className="the-new-login-button-campus">
                        <button type="button" onClick={this.handleStepChange}>
                        <div className="sso-provider-items">
                          <EnvelopeIcon size={24} className="the-new-login-button-campus-icon"/>
                          <p>{intl.formatMessage(messages['intro.page.button.text.login'])}</p>
                        </div>
                        </button>
                      </div>
                      </>
                    )}
                    {Boolean(this.props.loginError) && (
                      <LoginFailureMessage loginError={this.props?.loginError} />
                    )}                   
                    {Boolean(this.state.isSecondStep) && (
                    <div className="d-flex campus-flex-wrap-wrap the-new-login-style-campus-input-box">
                      <div className="the-new-login-style-campus-input-box-single">
                      <FormGroup
                        inputClassName="campus-width-100"
                        name="emailOrUsername"
                        value={this.state.emailOrUsername}
                        autoComplete="on"
                        handleChange={(e) => this.setState({ emailOrUsername: e.target.value /* isSubmitted: false */ })}
                        // handleFocus={this.handleOnFocus}
                        borderClass="font-weight-600"
                        handleBlur={this.handleOnBlur}
                        errorMessage={this.state.errors.emailOrUsername}
                        placeholder={intl.formatMessage(messages['login.page.email.input.placeholder'])}
                        floatingLabel={intl.formatMessage(messages['login.page.email.input.placeholder'])}
                      />
                      </div>
                      <div className="d-flex flex-column campus-width-100 the-new-login-style-campus-input-box-single">
                        <PasswordField
                          inputClassName="campus-width-100"
                          name="password"
                          value={this.state.password}
                          autoComplete="off"
                          showRequirements={false}
                          borderClass="font-weight-600"
                          handleChange={(e) => this.setState({ password: e.target.value /* isSubmitted: false */ })}
                          // handleFocus={this.handleOnFocus}
                          handleBlur={this.handleOnBlur}
                          errorMessage={this.state.errors.password}
                          floatingLabel={intl.formatMessage(messages['login.page.password.input.placeholder'])}
                          placeholder={intl.formatMessage(messages['login.page.password.input.placeholder'])}
                        />
                        <p className="regular-text mb-0 the-new-login-style-campus-forgot-password">
                          {intl.formatMessage(messages['login.page.forgot.password.text'])} <Link className="link-style the-new-login-style-campus-forgot-link" type="button" to={updatePathWithQueryParams(RESET_PAGE)} onClick={this.handleForgotPasswordLinkClickEvent}>{intl.formatMessage(messages['login.page.reset.link'])}</Link>
                        </p>
                      </div>
                    </div>
                    )}
                  </div>
                </div>
                {Boolean(this.state.isSecondStep) && (
                <div className="flex-center-column">
                  <StatefulButton
                    name="sign-in"
                    id="sign-in"
                    type="submit"
                    variant="brand"
                    className="logistration-btn"
                    state={submitState}
                    labels={{
                      default: intl.formatMessage(messages['login.page.button.text']),
                      pending: '',
                    }}
                    onClick={this.handleSubmit}
                    onMouseDown={(e) => e.preventDefault()}
                  />
                  <p className="regular-text mt-4">
                    {intl.formatMessage(messages['login.page.not.registered.text'])} <button className="link-style" type="button" onClick={this.props.onSelectPage}>{intl.formatMessage(messages['login.page.sign.up.here.text'])}</button>
                  </p>
                </div>
                )}
              </Form>
            </div>
          </div>
        </div>
      </>
    );
  }

  render() {
    const {
      intl, submitState, thirdPartyAuthContext, thirdPartyAuthApiStatus,
    } = this.props
    const { currentProvider, providers, secondaryProviders } = this.props.thirdPartyAuthContext;
    const { loginError } = this.props;
    const { inactiveUserStep } = this.state;

    // Reactivating an inactive account (see the INACTIVE_USER branch below) takes
    // priority over the tpaHint fast-path, even when we arrived here via an SSO
    // pipeline (tpaHint is set from a `tpa_hint` URL param present specifically
    // on that path). Previously `if (this.tpaHint)` always won and returned early
    // without ever looking at loginError, so binding Google SSO to an existing
    // but inactive account could never reach the OTP-reactivation flow at all -
    // login_session correctly returned error_code: 'inactive-user', but the UI
    // just fell back to the plain login form every time, as if nothing had
    // happened.
    const isReactivatingInactiveUser = loginError?.errorCode === INACTIVE_USER
      && loginError?.context?.token
      && this.state.showInactiveFlow;

    if (this.tpaHint && !isReactivatingInactiveUser) {
      if (thirdPartyAuthApiStatus === PENDING_STATE) {
        return <Skeleton height={36} />;
      }
      const { provider, skipHintedLogin } = getTpaProvider(this.tpaHint, providers, secondaryProviders);
      if (skipHintedLogin) {
        window.location.href = getConfig().LMS_BASE_URL + provider.loginUrl;
        return null;
      }
      return provider ? (<HintedSSOOnboarding provider={provider} intl={intl} />) : this.renderForm(
        currentProvider,
        providers,
        secondaryProviders,
        thirdPartyAuthContext,
        thirdPartyAuthApiStatus,
        submitState,
        intl,
      );
    }

    if (isReactivatingInactiveUser) {
      return (
        <div className="form-w">
          <div className="d-flex flex-column w-100 h-100 form-login-register-wrapper">
            <div className="flex-center-column form-login-register-background logistration-full-width">
              {inactiveUserStep === 'otp_verification' && (
                <EmailVerification
                  formFields={{ email: loginError.context.email }}
                  initialToken={loginError.context.token}
                  handleGoToNextStep={() => {
                    this.props.setAuthenticatedUser({
                      username: loginError.context.username,
                      email: loginError.context.email,
                      name: loginError.context.name,
                    });

                    if (this.props.thirdPartyAuthContext.currentProvider) {
                      // Reactivating an existing, inactive account as part of
                      // binding a third-party provider (e.g. Google SSO) to it -
                      // the account is now active and the user is logged in
                      // server-side, but the SSO pipeline itself hasn't been
                      // completed yet. Re-fetch the pipeline context now that
                      // we're authenticated; componentDidUpdate above redirects
                      // to finishAuthUrl once it comes back, instead of falling
                      // through to the plain registration-completion wizard,
                      // which has no way to finish an SSO link.
                      this.setState({ inactiveUserStep: 'completing_pipeline' });
                      this.props.getThirdPartyAuthContext({ ...this.queryParams, is_register_page: true });
                      return;
                    }

                    this.props.getThirdPartyAuthContext({ ...this.queryParams, is_register_page: true });
                    this.setState({ inactiveUserStep: 'auth_success' });
                  }}
                  handleGoToPrevStep={() => this.setState({ showInactiveFlow: false })}
                />
              )}
              {inactiveUserStep === 'completing_pipeline' && (
                <Skeleton height={36} />
              )}
              {inactiveUserStep === 'auth_success' && (
                <RegistrationConfirmation
                  formFields={{ email: loginError.context.email, full_name: loginError.context.name }}
                  queryParams={this.queryParams}
                  currentProvider={null}
                  handleGoToNextStep={() => {
                    this.setState({ inactiveUserStep: 'additional_info' });
                  }}
                />
              )}
              {inactiveUserStep === 'additional_info' && (
                <AdditionalUserInformation
                  handleGoToNextStep={() => {
                    this.setState({ inactiveUserStep: 'completion' });
                  }}
                  handleGoToPrevStep={() => {
                    this.setState({ inactiveUserStep: 'auth_success' });
                  }}
                />
              )}
              {inactiveUserStep === 'completion' && (
                <RegistrationProcessCompletion
                  formFields={{ email: loginError.context.email, full_name: loginError.context.name }}
                  queryParams={this.queryParams}
                />
              )}
            </div>
          </div>
        </div>
      );
    }

    return this.renderForm(
      currentProvider,
      providers,
      secondaryProviders,
      thirdPartyAuthContext,
      thirdPartyAuthApiStatus,
      submitState,
      intl,
    );
  }
}

LoginPage.defaultProps = {
  loginResult: null,
  loginError: null,
  loginFormData: {
    emailOrUsername: '',
    password: '',
    errors: {
      emailOrUsername: '',
      password: '',
    },
  },
  resetPassword: false,
  submitState: DEFAULT_STATE,
  isFirstStap: false,
  thirdPartyAuthApiStatus: 'pending',
  thirdPartyAuthContext: {
    currentProvider: null,
    errorMessage: null,
    finishAuthUrl: null,
    providers: [],
    secondaryProviders: [],
  },
};

LoginPage.propTypes = {
  getThirdPartyAuthContext: PropTypes.func.isRequired,
  intl: PropTypes.shape({
    formatMessage: PropTypes.func,
  }).isRequired,
  onSelectPage: PropTypes.func.isRequired,
  loginError: PropTypes.shape({}),
  loginRequest: PropTypes.func.isRequired,
  loginRequestFailure: PropTypes.func.isRequired,
  loginRequestReset: PropTypes.func.isRequired,
  setLoginFormData: PropTypes.func.isRequired,
  loginRemovePasswordResetBanner: PropTypes.func.isRequired,
  loginResult: PropTypes.shape({
    redirectUrl: PropTypes.string,
    success: PropTypes.bool,
  }),
  loginFormData: PropTypes.shape({
    emailOrUsername: PropTypes.string,
    password: PropTypes.string,
    errors: PropTypes.shape({
      emailOrUsername: PropTypes.string,
      password: PropTypes.string,
    }),
  }),
  resetPassword: PropTypes.bool,
  submitState: PropTypes.string,
  thirdPartyAuthApiStatus: PropTypes.string,
  thirdPartyAuthContext: PropTypes.shape({
    currentProvider: PropTypes.string,
    errorMessage: PropTypes.string,
    platformName: PropTypes.string,
    providers: PropTypes.arrayOf(PropTypes.shape({})),
    secondaryProviders: PropTypes.arrayOf(PropTypes.shape({})),
    finishAuthUrl: PropTypes.string,
  }),
  institutionLogin: PropTypes.bool.isRequired,
  handleInstitutionLogin: PropTypes.func.isRequired,
  redirectTarget: PropTypes.string,
  clearRedirectTarget: PropTypes.func.isRequired,
};

const mapStateToProps = state => {
  const loginResult = loginRequestSelector(state);
  const thirdPartyAuthContext = thirdPartyAuthContextSelector(state);
  const loginError = loginErrorSelector(state);
  const loginFormData = loginFormDataSelector(state);
  return {
    submitState: state.login.submitState,
    thirdPartyAuthApiStatus: state.commonComponents.thirdPartyAuthApiStatus,
    loginError,
    loginResult,
    thirdPartyAuthContext,
    loginFormData,
    resetPassword: state.login.resetPassword,
    redirectTarget: state.commonComponents.redirectTarget,
  };
};

export default connect(
  mapStateToProps,
  {
    getThirdPartyAuthContext,
    loginRequest,
    loginRequestFailure,
    loginRequestReset,
    setLoginFormData,
    loginRemovePasswordResetBanner,
    clearRedirectTarget,
    setAuthenticatedUser,
  },
)(injectIntl(LoginPage));
